import React, { useEffect, useRef, useState } from 'react';
import SlimSelect from 'slim-select';
import { Option } from 'slim-select/dist/data.d';
import { useTranslation } from 'react-i18next';
import { cloneDeep } from 'lodash';

import { isMobileScreenSize } from '../../../core/utils/isMobileScreenSize';

import './Select.scss';

interface Props {
  label?: string;
  id?: string;
  name?: string;
  value?: string;
  onChange?: ((info: Option | Option[]) => void) | undefined;
  options?: Option[];
  addPlaceholderOption?: boolean;
  placeholder?: string;
  className?: string;
  required?: boolean;
  disabled?: boolean;
  showSearch?: boolean;
  error?: any;
  showErrorMessage?: boolean;
}

const processOptions = (options: Option[] | undefined, value: string | undefined, addPlaceholderOption: boolean, placeholderText?: string) => {
  if (!options) {
    return [];
  }

  let optionsClone = cloneDeep(options);
  if (value) {
    const selectedOption = optionsClone.find((o) => o.value === value);
    if (selectedOption) {
      selectedOption.selected = true;
    }
  }
  if (addPlaceholderOption) {
    optionsClone = [
      {
        text: placeholderText ?? '',
        selected: value === undefined
      },
      ...optionsClone
    ];
  }
  return optionsClone;
};

const Select: React.FC<Props> = ({
  label,
  name,
  id,
  onChange,
  options: initialOptions,
  value,
  addPlaceholderOption = false,
  placeholder,
  className,
  required,
  disabled,
  showSearch = true,
  error,
  showErrorMessage = true
}) => {
  const hasErrorMessage = showErrorMessage && error && typeof error === 'string';
  const { t } = useTranslation();
  const divElement = useRef(null);
  const [selectObject, setSelectObject] = useState<SlimSelect | null>(null);
  const placeholderLabel = placeholder ?? t('common.select');
  const [options, setOptions] = useState<Option[]>(processOptions(initialOptions, value, addPlaceholderOption, placeholderLabel));
  const isMobileScreen = isMobileScreenSize();

  useEffect(() => {
    if (divElement.current && selectObject === null) {
      const slimSelect = new SlimSelect({
        select: divElement.current,
        placeholder: placeholderLabel,
        showSearch,
        searchingText: t('common.searching'),
        onChange,
        data: options,
        hideSelectedOption: true,
        allowDeselectOption: true
      });
      setSelectObject(slimSelect);
    }

    return () => {
      selectObject?.destroy();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setOptions(processOptions(initialOptions, value, addPlaceholderOption, placeholderLabel));
  }, [addPlaceholderOption, initialOptions, t, value, placeholderLabel]);

  useEffect(() => {
    selectObject?.setData(options);
  }, [options, selectObject]);

  useEffect(() => {
    if (disabled) {
      selectObject?.disable();
    } else {
      selectObject?.enable();
    }
  }, [disabled, selectObject]);

  return (
    <div className={`select ${className ?? ''} ${isMobileScreen && 'select--mobile'}`}>
      {label && (
        <label className="input__label" htmlFor={id}>
          {label}
          {required && '*'}
        </label>
      )}
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label */}
      <select id={id} name={name} ref={divElement} required={required} disabled={disabled} />
      {hasErrorMessage && <div className="input__error">{error}</div>}
    </div>
  );
};

export default Select;
